import { createAction } from '@reduxjs/toolkit'
import { call, put, select, throttle } from 'redux-saga/effects'

import { updateFlagOpenings } from '../../../api/projects-api'
import { FlagOpeningLinksAPIUpdatePayload, NormalizedFlag } from '../../../models/flags'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Flag, Workspace } from '../../lib/toolBoxes/2D'
import { updateToolbarMessage } from '../../slices/common'
import { selectMaterialFlags, updateFlagOpeningCoordinates } from '../../slices/materialFlags'
import { SAGA_THROTTLE_TIMER, SAVING_MESSAGE } from '../../types'

export const updateFlagCoordinatesAction = createAction<{ id: number; openingId: number | null; paperId: number }>(
    'updateFlagCoordinates'
)

export function* handleUpdateFlagCoordinates({ payload }: ReturnType<typeof updateFlagCoordinatesAction>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

    const movedFlag: paper.Item | null = yield call(workspaceTool.getItemWithPaperId, payload.paperId)

    if (!movedFlag) return

    const flagTool: Flag = yield call(manager.getTool, Flag.NAME)

    const flagGroups: paper.Group[] = yield call(flagTool.getCachedFlags)

    const flagGroupInQuestion: paper.Group | undefined = flagGroups.find((group) => group.data.flag_id === payload.id)

    if (!flagGroupInQuestion) return

    const flags: NormalizedFlag[] = yield select(selectMaterialFlags)

    const flagInQuestion: NormalizedFlag | undefined = flags.find((flag) => flag.id === payload.id)

    if (!flagInQuestion) return

    yield put(updateToolbarMessage(SAVING_MESSAGE))

    const openingLinks = flagInQuestion.opening_links.reduce((oLinks, oL) => {
        // Only update the specific opening_link that was moved
        if (oL.id === movedFlag.data.id) {
            oLinks.push({
                id: oL.id,
                flag_id: payload.id,
                coordinates: flagTool.getFlagCoordinates(movedFlag) as [number, number],
                opening_id: oL.opening_id || undefined,
            })
        }

        return oLinks
    }, [] as FlagOpeningLinksAPIUpdatePayload[])

    yield call(updateFlagOpenings, { opening_links: openingLinks })

    yield put(updateFlagOpeningCoordinates({ newOpeningLinkData: openingLinks }))

    yield put(updateToolbarMessage(null))
}

export function* watchForUpdateFlagCoordinates() {
    yield throttle(SAGA_THROTTLE_TIMER, updateFlagCoordinatesAction.type, handleUpdateFlagCoordinates)
}
